import { z } from 'zod';

export const purchaseOrderStatusSchema = z.enum(['draft', 'ordered', 'cancelled']);
export const purchaseOrderPaymentStatusSchema = z.enum(['unpaid', 'paid', 'refunded']);
export const purchaseOrderDeliveryStatusSchema = z.enum(['pending', 'shipping', 'received']);

export const purchaseOrderItemSchema = z.object({
  id: z.string().optional(), // Line Item ID for updates
  inventoryItemId: z.string().optional(),
  itemCode: z.string().optional(),
  name: z.string().min(1, 'Item name is required'), // Helper for display
  quantity: z.number()
    .min(0.001, 'Weight must be greater than 0')
    .refine((value) => Math.round(value * 1000) / 1000 === value, 'Max 3 decimal places'), // Weight (kg)
  weightReceived: z.number()
    .optional()
    .nullable()
    .refine((value) => value == null || Math.round(value * 1000) / 1000 === value, 'Max 3 decimal places'),
  unitPrice: z.number().min(0, 'Unit Price must be non-negative'),
  lotId: z.string().optional(),
  lotNumber: z.string().optional(),
  sampleItem: z.boolean().optional().default(false),
  sampleId: z.string().optional(), // Added for Sample integration
}).refine(data => data.inventoryItemId || data.name, {
  message: "Item is required",
  path: ["inventoryItemId"]
}).refine(
  data => data.weightReceived == null || data.weightReceived <= data.quantity,
  {
    message: 'Arrival weight cannot exceed buy weight',
    path: ['weightReceived'],
  }
);

export type PurchaseOrderItem = z.infer<typeof purchaseOrderItemSchema>;

export const purchaseOrderExpenseSchema = z.object({
  expenseType: z.string(),
  itemId: z.string().optional(),
  itemCode: z.string().optional(),
  itemName: z.string().optional(),
  quantityLoss: z.number().min(0),
  unitPrice: z.number().min(0),
  amount: z.number().min(0),
  description: z.string().optional(),
});

export type PurchaseOrderExpense = z.infer<typeof purchaseOrderExpenseSchema>;

export const purchaseOrderSchema = z.object({
  supplier: z.string().min(1, 'Supplier is required'),
  orderDate: z.string().min(1, 'Order Date is required'),
  expectedDeliveryDate: z.string().optional().nullable(),
  actualDeliveryDate: z.string().optional().nullable(),
  status: purchaseOrderStatusSchema,
  paymentStatus: purchaseOrderPaymentStatusSchema.optional(),
  deliveryStatus: purchaseOrderDeliveryStatusSchema.optional(),
  paymentType: z.string().min(1, 'Payment method is required'),
  paymentNotes: z.string().optional(),
  paymentProofUrl: z.string().optional().nullable(),
  notes: z.string().optional(),
  internalNotes: z.string().optional(),
  items: z.array(purchaseOrderItemSchema).min(1, 'At least one item is required'),
  purchaseExpenses: z.array(purchaseOrderExpenseSchema).optional(),
}).superRefine((data, ctx) => {
  const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;

  if (!data.orderDate || !dateFormatRegex.test(data.orderDate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Order Date is invalid',
      path: ['orderDate'],
    });
    return;
  }

  if (!data.actualDeliveryDate) return;

  if (!dateFormatRegex.test(data.actualDeliveryDate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Actual Delivery Date is invalid',
      path: ['actualDeliveryDate'],
    });
    return;
  }

  if (data.actualDeliveryDate < data.orderDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Actual Delivery Date must be later than or equal to Order Date',
      path: ['actualDeliveryDate'],
    });
  }
});

export type PurchaseOrderFormData = z.infer<typeof purchaseOrderSchema>;

export interface PurchaseOrder extends PurchaseOrderFormData {
  id: string;
  orderNumber: string;
  totalAmount: number;
  supplierName?: string; // Added for display
  createdAt: string;
  updatedAt: string;
}
